import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { bankRequisitesService } from '../../../api/services/bankRequisites';
import { BankRequisites as BankRequisitesType } from '../../../types/bankRequisites';
import { useSnack } from '../../../utils/useSnack';
import { State } from '../../../redux/store';
import Container from '../../../common/Container';
import { useTranslation } from 'react-i18next';
import { BankRequisiteModal } from './components';
import NoRowsOverlay from '../../../common/NoRowsOverlay';
import {ActionColumn, Column, columnStyle, DateTimeColumn} from "../../../common/Columns";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import {PlanState} from "../../../utils/enums";

export default function BankRequisites() {
  const { t } = useTranslation();
  const { snack } = useSnack();
  const state = useSelector((state: State) => state);

  const [loading, setLoading] = useState(false);
  const [bankRequisites, setBankRequisites] = useState<BankRequisitesType[]>([]);
  const company = state.company.company;

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRequisite, setSelectedRequisite] = useState<BankRequisitesType | undefined>(undefined);

  const getBankRequisites = async () => {
    if (!company) return;

    try {
      setLoading(true);
      const response = await bankRequisitesService.getBankRequisites(company.id);

      if (response.status === "success" && response.data) {
        setBankRequisites(response.data);
      } else {
        snack(t("snack.error.bank_requisites"), "error");
      }
    } catch (err) { 
      snack(t("snack.error.bank_requisites"), "error");
    } finally { 
      setLoading(false);
    }
  };

  const handleEditClick = (requisite: BankRequisitesType) => {
    setSelectedRequisite(requisite);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = async (requisite: BankRequisitesType) => {
    if (!company) return;

    try {
      const response = await bankRequisitesService.deleteBankRequisite(company.id, requisite.id);

      if (response.status === "success") {
        snack(t("snack.success.bank_requisite_deleted"), "success");
        getBankRequisites();
      } else {
        snack(t("snack.error.delete_bank_requisite"), "error");
      }
    } catch (err) {
      snack(t("snack.error.delete_bank_requisite"), "error");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const actions = [
    {
      label: "action.remove",
      callback: async (row: any) => {
        // Логика удаления плана. В данном примере используется подтверждение удаления.
        await handleDeleteClick(row);
      },
      predicate: (row: any) => {
        // Здесь можно задать условие доступности действия для конкретной записи.
        // Например, вернуть true, если удаление разрешено для данного плана.
        return true;
      }
    },
    {
      label: "action.approve",
      callback: async (row: any) => {
        // Логика удаления плана. В данном примере используется подтверждение удаления.
        await handleEditClick(row);
      },
      predicate: (row: any) => {
        // Здесь можно задать условие доступности действия для конкретной записи.
        // Например, вернуть true, если удаление разрешено для данного плана.
        return true;
      }
    }]
  const columns: GridColDef[] = [
    {... ActionColumn(100, columnStyle, "cabinet.plans", actions),},
    { ...Column("bik", 150, columnStyle, t("cabinet.bank_requisites.bik")), width: 150 },
    { ...Column("iik", 200, columnStyle, t("cabinet.bank_requisites.iik")), width: 200 },
    { ...Column("name", 300, columnStyle, t("cabinet.bank_requisites.name")), width: 300 },
    { ...DateTimeColumn("createdAt", 200, columnStyle, "cabinet.bank_requisites.created_at")},
  ];


  useEffect(() => {
    if (company?.id) {
      getBankRequisites();
    }
  }, [company]);

  return (
    <Box>
      <BankRequisiteModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        onSuccess={getBankRequisites} 
      />

      <BankRequisiteModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        onSuccess={getBankRequisites}
        editMode={true}
        requisite={selectedRequisite}
      />

      <Container paddingY={{ xs: 2, sm: 4 }} maxWidth="unset">
        <Box marginBottom={4}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            {t("cabinet.bank_requisites.title")}
          </Typography>
        </Box>

        <Grid item container spacing={2} alignItems={"center"} mb={2}>
          <Grid item xs={12} sm={5} md={4} lg={3}>
            <Button 
              fullWidth 
              color="primary" 
              variant="contained"
              onClick={() => setIsAddModalOpen(true)}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                {t("cabinet.bank_requisites.add_new")}
              </span>
            </Button>
          </Grid>
        </Grid>

        <Box>
          <DataGrid 
            components={{ NoRowsOverlay: () => <NoRowsOverlay /> }}
            sx={{
              height: 500,
              "& .MuiDataGrid-cell": {
                whiteSpace: "pre-wrap"
              },
            }}
            columnBuffer={5}
            rows={bankRequisites} 
            columns={columns}
            loading={loading}
            density="comfortable" 
            style={{ whiteSpace: "pre-wrap" }}
          />
        </Box>
      </Container>
    </Box>
  );
}
